import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { produce } from 'immer';

import { bankFeedApi } from 'domain/bankFeed/api';
import type { EndpointBody, EndpointResponse } from 'services/api';
import { queryOptions, useApiMutation } from 'services/query';
import { handleErrorResponseWithToast, yieldSuccessNotification } from 'lib/toasts';
import { bankFeedKeys } from './keys';

export type ConnectionsCache = EndpointResponse<typeof bankFeedApi.getConnections>;

export const bankFeedConnectionsQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.connections(companyId),
    queryFn: () => bankFeedApi.getConnections(),
  });

export const useBankFeedConnections = (companyId: string) => useQuery(bankFeedConnectionsQuery(companyId));

export const useDeleteBankFeedConnectionMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.deleteConnection,
    onSuccess: (_, { path }) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.filter((row) => row.id !== path.id);
        }),
      );
      yieldSuccessNotification({
        id: 'configurations.company.bankFeed.tabs.connections.toast.connection.delete.success',
        defaultMessage: 'Connection deleted successfully!',
      });
    },
    onError: handleErrorResponseWithToast,
  });
};

export const useUpdateBankFeedConnectionMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.updateConnection,
    onSuccess: (updatedRow, { body }) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        }),
      );
      yieldSuccessNotification(
        body?.suspended
          ? {
              id: 'configurations.company.bankFeed.tabs.connections.toast.connection.suspend.success',
              defaultMessage: 'Connection suspended successfully!',
            }
          : {
              id: 'configurations.company.bankFeed.tabs.connections.toast.connection.unsuspend.success',
              defaultMessage: 'Connection unsuspended successfully!',
            },
      );
    },
    onError: handleErrorResponseWithToast,
  });
};

// Create returns { connection_url, connection } — connection_url drives the
// bank-auth iframe (consumer reads it from mutation.data on success). The new
// row is NOT written into the connections cache here: it arrives via the
// WebSocket batch processor → getConnectionsByIds → setQueryData (see
// pages/.../Connections/sagas.ts). No global onError either — Step3 owns the
// error UI for the bank-auth flow.
//
// Per-call error type override (per the constitution in services/query/CLAUDE.md):
// Step3 reads `error.response.data.message` for the localized banner, so we
// narrow the axios payload to that shape here instead of casting at the use site.
type CreateConnectionError = AxiosError<{ message?: string }>;

export const useCreateBankFeedConnectionMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.createConnection>,
    CreateConnectionError,
    { body: EndpointBody<typeof bankFeedApi.createConnection> }
  >({ mutationFn: bankFeedApi.createConnection });

// Create-and-delegate in one write, also used by "Resend authorization link".
// No cache writes — the created connection arrives via the BANK_CONNECTION_*
// WebSocket events. Toasts live here so both dialogs share them.
export const useDelegateBankFeedConnectionMutation = () =>
  useApiMutation({
    mutationFn: bankFeedApi.delegateConnection,
    onSuccess: (_data, { body }) => {
      yieldSuccessNotification(
        body.channel === 'EMAIL'
          ? {
              id: 'modal.bankFeed.connection.add.delegate.toast.email',
              defaultMessage: 'Email was sent successfully!',
            }
          : {
              id: 'modal.bankFeed.connection.add.delegate.toast.whatsapp',
              defaultMessage: 'WhatsApp message was sent successfully!',
            },
      );
    },
    onError: (error) =>
      handleErrorResponseWithToast(error, {
        defaultMessage: {
          id: 'modal.bankFeed.connection.add.createError',
          defaultMessage: 'Something went wrong. Please try again.',
        },
      }),
  });

export const useSyncBankFeedTransactionsMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.syncTransactions,
    onSuccess: (updatedRow) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        }),
      );
      yieldSuccessNotification({
        id: 'configurations.company.bankFeed.tabs.connections.toast.syncTransactions.success',
        defaultMessage: 'Bank transaction synchronization has started.',
      });
    },
    onError: handleErrorResponseWithToast,
  });
};
